import { InputValidatorType, plainText, richText } from '../../../../modules/validators';
import { trimSpacesLeft } from '../../../../modules/formatters';
import { FormVmItemConfigType, FormVmValueValidatorResultType, InitialData } from '../../../../infrastructure/form-viewmodel-controller';
import { S3ObjectMetadata } from '../../../../api/shared.dto';
import { DESCRIPTION as DESCRIPTION_LABEL, EXTERNAL_ID_TEXT } from '../../../../constants/common-labels.constants';
import { buildConfigList } from '../../../../infrastructure/utils/form-config.utils';
import { MEETING_TEMPLATE_TEXT } from '../../../_events/_manage-event/_meetings-list/_meeting-form/meeting-form.texts';
import { DEFAULT_MEETING_TEMPLATE_TEXT } from '../../blueprints.texts';

export enum FieldKey {
    NAME = 'name',
    DESCRIPTION = 'description',
    TIME_ZONE = 'timeZone',
    REGION = 'region',
    BUSINESS_LINE = 'businessLine',
    TYPE = 'type',
    EVENT_ACCESS = 'eventAccess',
    DEFAULT_MEETING_DURATION = 'defaultMeetingDuration',
    CREATE_PASSPORT_SITE = 'createPassportSite',
    CREATE_SINGLE_MEETING = 'createSingleMeeting',
    MEETING_TEMPLATE = 'meetingBlueprintId',
    PASSPORT_CONFIG = 'passportConfig',
    PASSPORT_URL_FORMAT = 'passportUrlFormat',
    INTEGRATION_ENABLED = 'integrationEnabled',
    EXTERNAL_ID = 'externalId',
    ATTENDEE_LINK_TYPE = 'attendeeLinkType',
    INTEGRATION_MEETING_TEMPLATE = 'integrationMeetingBlueprintId',
    ORDER_BOOKING_ENABLED = 'orderBookingEnabled',
    PUBLIC_NAME = 'publicName',
    PUBLIC_DESCRIPTION = 'publicDescription',
    PARTICIPANT_LINK_TYPE = 'participantLinkType',
    ORDER_BOOKING_SUBSET = 'orderBookingSubset',
    DEFAULT_MEETING_BLUEPRINT_ID = 'defaultMeetingBlueprintId',
    DEFAULT_MEETING_BLUEPRINT_ENABLED = 'defaultMeetingBlueprintEnabled',
    EVENT_ATTRIBUTE_IDS = 'eventAttributeIds',
}

export enum ParticipantLinkType {
    PASSPORT_LINK = 'PASSPORT_LINK',
    MEETING_LINK_WITH_EMBEDDED_PASSCODE = 'MEETING_LINK_WITH_EMBEDDED_PASSCODE'
}

export const meetingDurationValidator: InputValidatorType = (val: number = 0): FormVmValueValidatorResultType => {
    const valid = val >= 0 && val <= 1440 && !Number.isNaN(val);
    const validationMessage = valid
        ? null
        : { id: 'oe.validation.meetingDuration.message', defaultMessage: 'Field value should be between 0 and 1440' }
        ;

    return { valid: valid, validationMessage: validationMessage };
};

export const passportConfigValidator = (val: S3ObjectMetadata, formValues: InitialData): FormVmValueValidatorResultType => {
    const valid = !formValues[FieldKey.CREATE_PASSPORT_SITE] || !!val;
    const validationMessage = valid
        ? null
        : { id: 'oe.validation.jsonConfigurationRequired.message', defaultMessage: 'You must upload a JSON configuration file.' }
        ;

    return { valid: valid, validationMessage: validationMessage };
};

export const conditionalPublicNameValidator = (val: string, formValues: InitialData): FormVmValueValidatorResultType => {
    if (!formValues[FieldKey.ORDER_BOOKING_ENABLED]) {
        return { valid: true, validationMessage: null };
    }

    if (!val || val.trim() === '') {
        return {
            valid: false,
            validationMessage: {
                id: 'oe.validation.required.message'
            }
        };
    }

    return { valid: true, validationMessage: null };
};

export const publicDescriptionMaxLengthValidator: InputValidatorType = (val: string = ''): FormVmValueValidatorResultType => {
    const valid = richText.maxLength.long(val).valid;
    const validationMessage = valid
        ? null
        : { id: 'oe.validation.publicDescriptionTooLong.message', defaultMessage: 'Blueprint public description is too long' }
        ;

    return { valid: valid, validationMessage: validationMessage };
};

export const defaultMeetingTemplateIdValidator = (val: string, formValues: InitialData): FormVmValueValidatorResultType => {
    const valid = !formValues[FieldKey.DEFAULT_MEETING_BLUEPRINT_ENABLED] || !!val;
    const validationMessage = valid
        ? null
        : { id: 'oe.validation.defaultMeetingTemplateIdRequired.message', defaultMessage: 'You must select a meeting template.' }
        ;

    return { valid: valid, validationMessage: validationMessage };
};

export const FORM_CONFIG = {
    [FieldKey.NAME]: {
        labelDescriptor: { id: 'oe.blueprints.event.name', defaultMessage: 'Name', description: 'Label text' },
        defaultValue: '',
        validators: [plainText.required, plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.DESCRIPTION]: {
        labelDescriptor: DESCRIPTION_LABEL,
        defaultValue: '',
        validators: [plainText.maxLength.long],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.TIME_ZONE]: {
        labelDescriptor: { id: 'oe.common.timeZone', defaultMessage: 'Time Zone', description: 'Label text' },
        defaultValue: '',
    },
    [FieldKey.REGION]: {
        labelDescriptor: { id: 'oe.event.region', defaultMessage: 'Region', description: 'Label text' },
        defaultValue: '',
    },
    [FieldKey.BUSINESS_LINE]: {
        labelDescriptor: { id: 'oe.event.businessLine', defaultMessage: 'Business Line', description: 'Label text' },
        defaultValue: '',
    },
    [FieldKey.TYPE]: {
        labelDescriptor: { id: 'oe.event.eventType', defaultMessage: 'Event Type', description: 'Label text' },
        defaultValue: '',
    },
    [FieldKey.EVENT_ACCESS]: {
        labelDescriptor: { id: 'oe.event.eventAccess', defaultMessage: 'Event Access', description: 'Label text' },
        defaultValue: '',
    },
    [FieldKey.CREATE_PASSPORT_SITE]: {
        defaultValue: false,
    },
    [FieldKey.CREATE_SINGLE_MEETING]: {
        defaultValue: false
    },
    [FieldKey.MEETING_TEMPLATE]: {
        labelDescriptor: { id: 'oe.blueprints.event.meetingTemplate', defaultMessage: 'Meeting Template', description: 'Label text' },
        defaultValue: ''
    },
    [FieldKey.DEFAULT_MEETING_DURATION]: {
        labelDescriptor: { id: 'oe.blueprints.event.defaultMeetingDuration', defaultMessage: 'Default Meeting Duration (minutes)', description: 'Label text' },
        defaultValue: 0,
        validators: [meetingDurationValidator]
    },
    [FieldKey.PASSPORT_CONFIG]: {
        labelDescriptor: { id: 'oe.blueprints.event.passportConfigFile', defaultMessage: 'Passport JSON file', description: 'Label text' },
        defaultValue: null,
        validators: [passportConfigValidator]
    },
    [FieldKey.PASSPORT_URL_FORMAT]: {
        labelDescriptor: { id: 'oe.blueprints.event.passportUrlFormat', defaultMessage: 'URL format', description: 'Label text' },
        defaultValue: null
    },
    [FieldKey.INTEGRATION_ENABLED]: {
        defaultValue: false
    },
    [FieldKey.EXTERNAL_ID]: {
        labelDescriptor: EXTERNAL_ID_TEXT,
        defaultValue: '',
        validators: [plainText.maxLength.short],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.ATTENDEE_LINK_TYPE]: {
        labelDescriptor: { id: 'oe.blueprints.event.attendeeLink', defaultMessage: 'Attendee Link', description: 'Label text' },
        defaultValue: null
    },
    [FieldKey.INTEGRATION_MEETING_TEMPLATE]: {
        labelDescriptor: MEETING_TEMPLATE_TEXT,
        defaultValue: ''
    },
    [FieldKey.ORDER_BOOKING_ENABLED]: {
        defaultValue: false
    },
    [FieldKey.PUBLIC_NAME]: {
        labelDescriptor: { id: 'oe.blueprints.event.publicName', defaultMessage: 'Event Blueprint Public Name', description: 'Label text' },
        defaultValue: '',
        validators: [plainText.maxLength.short, conditionalPublicNameValidator],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.PUBLIC_DESCRIPTION]: {
        labelDescriptor: { id: 'oe.blueprints.event.publicDescription', defaultMessage: 'Event Blueprint Public Description', description: 'Label text' },
        defaultValue: '',
        validators: [publicDescriptionMaxLengthValidator],
        formatters: [trimSpacesLeft]
    },
    [FieldKey.PARTICIPANT_LINK_TYPE]: {
        labelDescriptor: { id: 'oe.blueprints.event.participantLink', defaultMessage: 'Participant Link', description: 'Label text' },
        defaultValue: ParticipantLinkType.PASSPORT_LINK
    },
    [FieldKey.EVENT_ATTRIBUTE_IDS]: {
        labelDescriptor: { id: 'oe.blueprints.event.eventAttributes', defaultMessage: 'Event Attributes', description: 'Label text' },
        defaultValue: [],
    },
    [FieldKey.ORDER_BOOKING_SUBSET]: {
        defaultValue: []
    },
    [FieldKey.DEFAULT_MEETING_BLUEPRINT_ENABLED]: {
        defaultValue: false
    },
    [FieldKey.DEFAULT_MEETING_BLUEPRINT_ID]: {
        labelDescriptor: DEFAULT_MEETING_TEMPLATE_TEXT,
        defaultValue: '',
        validators: [defaultMeetingTemplateIdValidator]
    },
};


export const configList: FormVmItemConfigType[] = buildConfigList(FORM_CONFIG);
